<template>
  <v-container v-if="loaded">
    <base-material-card
      :icon="eventProfile.type === 'unique' ? 'mdi-repeat-once' : 'mdi-repeat'"
      color="primary"
      inline
      v-if="eventProfile"
      class="px-5 my-12 py-3"
    >
      <template v-slot:after-heading>
        <div
          :class="$vuetify.breakpoint.mdAndUp ? 'd-flex' : 'd-flex flex-column'"
        >
          <v-chip
            v-if="eventProfile.equipmentProfile"
            label
            color="primary"
            dark
            class="mr-2 mb-2"
          >
            {{ $t("taskProfile.labels.tag") }}:
            {{ eventProfile.equipmentProfile.equipTag }}
          </v-chip>

          <v-chip v-else class="mr-2 mb-2" label color="primary" dark>
            {{ $t("taskProfile.labels.global") }}
          </v-chip>

          <v-chip
            v-if="eventProfile.type === 'recurrent'"
            color="secondary"
            class="mr-2 mb-2"
            dark
          >
            {{ $t("taskProfile.labels.recurrent") }}
          </v-chip>
          <v-chip
            v-if="eventProfile.type === 'unique'"
            color="secondary"
            class="mr-2 mb-2"
            dark
          >
            {{ $t("taskProfile.labels.onetime") }}
          </v-chip>

          <v-chip
            v-if="eventProfile.subtype === 'maintenance'"
            label
            color="accent"
            class="mr-2 mb-2"
            dark
          >
            {{ $t("taskProfile.labels.maintenance") }}
          </v-chip>
          <v-chip
            v-if="eventProfile.subtype === 'repair'"
            label
            color="accent"
            class="mr-2 mb-2"
            dark
          >
            {{ $t("taskProfile.labels.repair") }}
          </v-chip>
          <v-chip
            v-if="eventProfile.subtype === 'reliability'"
            label
            color="accent"
            class="mr-2 mb-2"
            dark
          >
            {{ $t("taskProfile.labels.reliability") }}
          </v-chip>

          <v-chip
            v-if="eventProfile.isDone"
            label
            color="success"
            class="mr-2 mb-2"
            dark
          >
            {{ $t("taskProfile.labels.done") }}
          </v-chip>
        </div>
      </template>

      <v-tooltip right>
        <template v-slot:activator="{ on }">
          <v-btn
            fab
            color="primary"
            fixed
            bottom
            left
            v-on="on"
            @click="$router.back()"
          >
            <v-icon>mdi-arrow-left</v-icon></v-btn
          >
        </template>
        <span>{{ $t("taskProfile.buttons.backbtn") }}</span>
      </v-tooltip>

      <v-container>
        <v-row dense justify="center" align="center">
          <v-col cols="12" md="12" justify="center" align="center">
            <v-text-field
              filled
              :readonly="!$store.getters['auth/equipmentWrite']"
              :rules="requireRules"
              v-model="eventProfile.name"
              :label="$t('taskProfile.name')"
            ></v-text-field>
          </v-col>
          <v-col
            v-if="eventProfile.inspectionId"
            cols="12"
            md="12"
            justify="center"
            align="center"
          >
            <v-btn
              @click="$router.push(`/inspections/${eventProfile.inspectionId}`)"
            >
              {{ $t("taskProfile.messages.gotoProfile") }}
            </v-btn>
          </v-col>
          <v-col
            v-if="$store.getters['auth/equipmentWrite']"
            cols="12"
            md="12"
            justify="center"
            align="center"
          >
            <v-autocomplete
              v-model="eventProfile.responsables"
              :items="users"
              multiple=""
              chips
              small-chips
              persistent-hint
              return-object
              hide-selected
              hide-details=""
              :item-text="fullName"
              item-color="success"
              prepend-icon="mdi-database-search"
            >
            </v-autocomplete>
          </v-col>
          <v-col
            v-if="eventProfile.responsables"
            cols="12"
            md="12"
            justify="center"
            align="start"
          >
            <v-card max-width="300" flat>
              <v-list v-if="$store.getters['auth/equipmentWrite']" outlined>
                <v-list-item
                  :to="`/public-profile/${item.id}`"
                  v-for="item in eventProfile.responsables"
                  :key="item.id"
                >
                  <v-list-item-content>
                    <v-list-item-title
                      v-text="fullName(item)"
                    ></v-list-item-title>
                  </v-list-item-content>

                  <v-list-item-icon>
                    <v-icon color="primary">
                      mdi-account-arrow-right-outline
                    </v-icon>
                  </v-list-item-icon>
                </v-list-item>
              </v-list>
              <v-list outlined v-else>
                <v-list-item
                  dense
                  v-for="item in eventProfile.responsables"
                  :key="item.id"
                >
                  <v-list-item-content>
                    <v-list-item-title
                      v-text="fullName(item)"
                    ></v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-icon>
                    <v-icon color="primary">
                      mdi-account-circle
                    </v-icon>
                  </v-list-item-icon>
                </v-list-item>
              </v-list>
            </v-card>
          </v-col>
          <v-col
            cols="12"
            md="12"
            justify="center"
            align="center"
            v-if="taskDate instanceof Date && isFinite(taskDate)"
          >
            <v-text-field
              filled
              :rules="requireRules"
              v-model="taskDate"
              label="Task Date"
              disabled
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="12" justify="center" align="center">
            <v-textarea
              auto-grow
              counter=""
              :readonly="!$store.getters['auth/equipmentWrite']"
              filled
              :rules="requireRules"
              v-model="eventProfile.description"
              :label="$t('taskProfile.labels.description')"
            ></v-textarea>
          </v-col>

          <template
            v-if="eventProfile.type === 'recurrent' && eventProfile.dateCreated"
          >
            <v-col cols="12" sm="6">
              <div class="body text-capitalize">
                <span class="font-weight-bold">
                  {{ $t("taskProfile.labels.createdOn") }}:</span
                >
                {{ eventProfile.dateCreated | formatDate }}
              </div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="body text-capitalize">
                <span class="font-weight-bold">
                  {{ $t("taskProfile.labels.due") }}:</span
                >
                {{ eventProfile.start }}
              </div>
            </v-col>
            <v-col sm="12" md="6">
              <span class="primary--text font-weight-bold"
                >{{ $t("taskProfile.labels.frequency") }}:</span
              >
              {{ $t("taskProfile.messages.frequency") }}
              {{ timeTooltip(eventProfile.changeTime) }}
            </v-col>
            <v-col sm="12" md="6">
              <span class="primary--text font-weight-bold">
                {{ $t("taskProfile.labels.notification") }}:</span
              >
              {{ $t("taskProfile.messages.notification") }}
              {{ timeTooltip(eventProfile.alertTime) }}
              {{ $t("taskProfile.messages.before") }}
            </v-col>
          </template>
          <template v-else>
            <v-col cols="12" sm="12">
              <div
                class="body text-capitalize"
                v-if="eventProfile && eventProfile.dateCreated"
              >
                <span class="font-weight-bold">
                  {{ $t("taskProfile.labels.createdOn") }}:</span
                >
                {{ eventProfile.dateCreated.toDate() | formatDate }}
              </div></v-col
            >
          </template>
          <v-col cols="12" class="d-flex">
            <v-spacer></v-spacer>
            <v-btn
              width="250"
              class="my-12 green"
              x-large
              v-if="!eventProfile.isDone"
              @click="doneTask"
              >{{ $t("taskProfile.buttons.done") }}</v-btn
            >
            <v-spacer></v-spacer>
          </v-col>
          <v-col cols="12" v-if="$store.getters['auth/equipmentWrite']">
            <v-divider class="my-12"></v-divider>
            <div class="title mb-4">{{ $t("taskProfile.labels.danger") }}</div>
            <v-row dense style="border:1px red solid;border-radius:8px">
              <v-col cols="12" class="d-flex flex-column flex-md-row pa-4">
                <div>
                  <div class="subtitle-1 font-weight-bold">
                    {{ $t("taskProfile.labels.deleteTask") }}
                  </div>
                  <div class="body">
                    {{ $t("taskProfile.messages.deleteTask") }}
                  </div>
                </div>
                <v-spacer></v-spacer>
                <v-dialog v-model="deleteModal" width="500">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn color="red lighten-2" dark v-bind="attrs" v-on="on">
                      {{ $t("taskProfile.buttons.delete") }}
                    </v-btn>
                  </template>

                  <v-card>
                    <v-card-title class="headline grey lighten-2">
                      <!-- TODO TRANSLATE -->
                      Are you sure?
                    </v-card-title>
                    <v-divider></v-divider>
                    <v-card-actions class="d-flex flex-column">
                      <v-spacer></v-spacer>
                      <template v-if="eventProfile.type === 'recurrent'">
                        <v-btn
                          class="mb-2"
                          outlined
                          block
                          color="error"
                          @click="deleteTask"
                        >
                          {{ $t("planner.buttons.deleteAll") }}
                        </v-btn>
                        <v-btn
                          outlined
                          class="mb-2"
                          block
                          color="error"
                          @click="deleteOnlyOne"
                        >
                          {{ $t("planner.buttons.deleteOnly") }}
                        </v-btn>
                      </template>
                      <v-btn
                        v-else
                        class="mb-2"
                        outlined
                        block
                        color="error"
                        @click="deleteTask"
                      >
                        {{ $t("taskProfile.buttons.delete") }}
                      </v-btn>
                      <v-btn
                        outlined
                        block
                        color="secondary"
                        @click="deleteModal = false"
                      >
                        {{ $t("planner.buttons.cancel") }}
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-dialog>
              </v-col>
              <v-col cols="12" class="d-flex flex-column flex-md-row pa-4">
                <div>
                  <div class="subtitle-1 font-weight-bold">
                    {{ $t("taskProfile.labels.updateTask") }}
                  </div>
                  <div class="body">
                    {{ $t("taskProfile.messages.updateTask") }}
                  </div>
                </div>
                <v-spacer></v-spacer>
                <v-btn color="info" @click="updateInfo()">
                  {{ $t("taskProfile.buttons.update") }}
                </v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row></v-container
      ></base-material-card
    >
    <v-divider class="my-12"></v-divider>
    <metax-chat class="my-12"></metax-chat>
  </v-container>
</template>

<script>
import { mapState } from "vuex";
import moment from "moment";
import { db } from "@/plugins/db";
export default {
  components: {
    "metax-chat": () => import("@/components/Tasks/Chat/Index.vue")
  },
  async created() {
    const loading = this.$vs.loading({
      background: "#730D0B",
      color: "rgb(255, 255, 255)",
      opacity: 1,
      type: "square"
    });
    try {
      await this.$store.dispatch("users/bindUsers");
      const res = await this.$store.dispatch(
        "calendar/bindAnEvent",
        this.$route.params.id
      );
      if (res.type === "recurrent") {
        this.events = await this.$store.dispatch(
          "calendar/bindEquipmentEvents",
          res.equipmentProfile.id
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.loaded = true;
    }
    this.lastDate = this.eventProfile.lastChange;
    loading.close();
  },
  data() {
    return {
      events: [],
      loaded: false,
      taskSubtypes: ["maintenance", "repair", "reliability"],
      uniqueId: this.$route.params.uniqueId,
      taskDate: this.$moment(this.$route.params.taskDate).format("DD-MM-YYYY"),
      deleteModal: false,
      colorChips: {
        CREATED: "success",
        UPDATE: "blue",
        REPAIR: "primary"
      },
      timeObject: [
        [this.$t("dateFormat.timeObjects.year"), 31536000],
        [this.$t("dateFormat.timeObjects.month"), 2592000],
        [this.$t("dateFormat.timeObjects.week"), 604800],
        [this.$t("dateFormat.timeObjects.day"), 86400],
        [this.$t("dateFormat.timeObjects.hour"), 3600],
        [this.$t("dateFormat.timeObjects.minute"), 60],
        [this.$t("dateFormat.timeObjects.second"), 1]
      ],
      requireRules: [v => !!v || "This field is mandatory."],
      lastDate: null
    };
  },
  computed: {
    ...mapState("calendar", ["eventProfile"]),
    ...mapState("auth", ["user"]),
    ...mapState("users", ["users"]),
    oldNextDate() {
      if (!this.eventProfile || !this.lastDate) return null;
      return this.formatDateWithTranslate(
        this.lastDate,
        this.eventProfile.changeTime
      );
    },
    nextDateInTimestamp() {
      if (!this.eventProfile || !this.eventProfile.lastChange) return null;
      return this.$moment(
        new Date(
          +new Date(this.eventProfile.lastChange) +
            this.eventProfile.changeTime * 1000
        )
      ).format("YYYY-MM-DD");
    },
    newChangeDate() {
      if (!this.eventProfile || !this.eventProfile.lastChange) return null;
      return this.formatDateWithTranslate(
        this.eventProfile.lastChange,
        this.eventProfile.changeTime
      );
    }
  },
  methods: {
    fullName(item) {
      return `${item.firstName} ${item.lastName}`;
    },
    doneTask: async function() {
      const res = await this.$dialog.confirm({
        text: "Do you want set as done this Task?",
        title: "Please Confirm"
      });
      if (res) {
        const loading = this.$vs.loading({
          background: "#730D0B",
          color: "rgb(255, 255, 255)",
          opacity: 1,
          type: "square"
        });
        const { type, disabledArray = [] } = this.eventProfile;
        const today = new Date().toISOString().substr(0, 10);
        if (type === "unique") {
          try {
            await db
              .collection("calendar")
              .doc(this.eventProfile.id)
              .update({
                isDone: true,
                start: today
              });
          } catch (err) {
            console.error(err);
          }
        }
        if (type === "recurrent") {
          let newDisabledList = [...disabledArray, this.uniqueId];
          let cloneTask = this.events.find(
            event => event.id === this.eventProfile.id
          );
          // * Cloning the event to crear a historical regiter
          let payload = {
            db: "calendar",
            data: {
              ...cloneTask,
              equipmentProfile: db
                .collection("equipments")
                .doc(cloneTask.equipmentProfile.id),
              start: today,
              end: today,
              lastChange: today,
              disabledArray: [],
              uniqueId: this.uniqueId,
              isDone: true,
              type: "unique"
            }
          };
          try {
            // * Updating the existing event to disabled the done one
            await db
              .collection("calendar")
              .doc(this.eventProfile.id)
              .update({ disabledArray: [...newDisabledList] });
            // * Creating the historical register
            await this.$store.dispatch("addDocument", payload);
          } catch (err) {
            console.error(err);
          }
        }
        this.$dialog.notify.success("Your task has been updated", {
          position: "top-right",
          timeout: 5000
        });
        loading.close();
      }
    },
    async updateInfo() {
      const res = await this.$dialog.confirm({
        text: "Do you want to update task information?",
        title: "Please Confirm"
      });
      if (res) {
        try {
          await this.$store.dispatch("updateDocument", {
            db: "calendar",
            doc: this.eventProfile.id,
            data: {
              ...this.eventProfile,
              equipmentProfile: db
                .collection("equipments")
                .doc(this.eventProfile.equipmentProfile.id)
            }
          });
          this.$dialog.notify.success("The task was updated successfully", {
            position: "top-right",
            timeout: 5000
          });
        } catch (error) {
          this.$dialog.notify.error(error, {
            position: "top-right",
            timeout: 5000
          });
        }
      }
    },
    rowClick: function(item) {
      this.$router.push({
        name: "RepairLogsProfile",
        params: { id: item.id }
      });
      //item  - selected item
    },
    formatDateWithTranslate: function(lastChange, timeChange) {
      const jsDate = +new Date(lastChange) + timeChange * 1000;
      const options = {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric"
      };
      const lang = this.user.language ? this.user.language : "en";
      return new Intl.DateTimeFormat(lang, options).format(jsDate);
    },
    deleteTask: async function() {
      const loading = this.$vs.loading({
        background: "#730D0B",
        color: "rgb(255, 255, 255)",
        opacity: 1,
        type: "square"
      });
      try {
        await db
          .collection("calendar")
          .doc(this.eventProfile.id)
          .update({
            disabled: true
          })
          .then(() => {
            this.$router.push({
              path: `/equipment/${this.eventProfile.equipmentProfile.id}/planner`
            });
          });
        this.$dialog.notify.success("Your task has been updated", {
          position: "top-right",
          timeout: 5000
        });
      } catch (error) {
        this.$dialog.notify.error(error, {
          position: "top-right",
          timeout: 5000
        });
      }
      loading.close();
    },
    deleteOnlyOne: async function() {
      const loading = this.$vs.loading({
        background: "#730D0B",
        color: "rgb(255, 255, 255)",
        opacity: 1,
        type: "square"
      });
      const { disabledArray = [] } = this.eventProfile;
      let newDisabledList = [...disabledArray, this.uniqueId];
      try {
        await db
          .collection("calendar")
          .doc(this.eventProfile.id)
          .update({ disabledArray: [...newDisabledList] })
          .then(() => {
            this.$router.push({
              path: `/equipment/${this.eventProfile.equipmentProfile.id}/planner`
            });
          });
        this.$dialog.notify.success("Your task has been updated", {
          position: "top-right",
          timeout: 5000
        });
      } catch (error) {
        this.$dialog.notify.error(error, {
          position: "top-right",
          timeout: 5000
        });
      }
      loading.close();
    },
    timeTooltip: function(time) {
      for (const [unit, value] of this.timeObject) {
        if (time > value || unit === "seconds") {
          let timeago = Math.floor(parseFloat(time / +value));
          if (timeago === 1) return `${timeago} ${unit}`;
          return `${timeago} ${unit}s`;
        }
      }
    }
  },
  filters: {
    FixingDate: function(value) {
      if (value) {
        return moment.unix(value.seconds).format("MMMM D YYYY, h:mm:ss a");
      } else {
        return null;
      }
    }
  }
};
</script>

<style></style>
